import { Skeleton } from '@mui/material';
import { getInitials } from '@tempo-io/string-util';
import { Avatar, AvatarFallback, AvatarGroup, AvatarImage } from '@tempo-io/sui-avatar';
import * as React from 'react';
import { OverflowTooltip } from '@/components/ui/OverflowTooltip';
import type { TempoUserResponse } from '@/lib/api/types/userManagement';
import { useRegisterVisibleUserIds, useVisibleUsers } from '../context/VisibleUsersContext';
import { CellTextSkeleton } from './withSkeleton';

export const MultiUserCell = ({ value }: { value?: string[] }) => {
  useRegisterVisibleUserIds(value);
  const { usersById, missingUserIds } = useVisibleUsers();

  const users = React.useMemo(
    () => (value ?? []).map((id) => usersById.get(id)),
    [value, usersById],
  );

  if (!value?.length) return null;

  const hasPending = value.some((id) => !usersById.has(id) && !missingUserIds.has(id));
  if (hasPending) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <Skeleton variant="circular" width={20} height={20} />
        <CellTextSkeleton />
      </div>
    );
  }

  const resolvedUsers = users.filter((u): u is TempoUserResponse => Boolean(u));
  if (!resolvedUsers.length) return null;

  const displayNames = resolvedUsers.map((user) => user.name || user.email || '');

  return (
    <div className="flex items-center gap-1 min-w-0">
      <AvatarGroup size="sm" className="flex-nowrap">
        {resolvedUsers.map((user) => {
          const displayName = user.name || user.email || '';
          return (
            <Avatar key={user.id} className="text-text-neutral-inverted">
              <AvatarImage src={user.pictureUrl} alt={displayName} />
              <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
            </Avatar>
          );
        })}
      </AvatarGroup>
      <OverflowTooltip
        tooltipContent={<span className="whitespace-pre-wrap">{displayNames.join('\n')}</span>}
      >
        {displayNames.join(', ')}
      </OverflowTooltip>
    </div>
  );
};
